// The ProteoRisk results: the overall line and facts, the body systems wheel,
// report highlights, a line per body system, and every condition grouped by
// body system with the proteins behind each moderate or high finding.
import React, { useMemo, useRef, useState } from 'react';

import { SECTIONS, proteinState, systemsByAttention } from './reportModel';
import {
  BodySystemWheel, ScoreBar, ScoreGauge, StatusIcon, SystemIcon, ZAxis, ZScoreBar,
} from './Visuals';
import {
  DOCTOR_NOTE, NO_PANEL, PROTEIN_STATE_LABEL, SCORE_FOOTNOTE, SECTION_COPY, STATUS_LABEL, TIER_LABEL,
  formatCount, formatPercent, formatSigned, groupNote, headline, highlightSentence, lowTitle,
  resultsNote, systemSentence,
} from '../copy';
import './proteorisk-report.css';

function Fact({ label, value, muted = false }) {
  return (
    <div className="PROTEORISK-fact">
      <span className="PROTEORISK-fact-label">{label}</span>
      <span className={`PROTEORISK-fact-value ${muted ? 'muted' : ''}`}>{value}</span>
    </div>
  );
}

function Facts({ report, facts }) {
  const { coverage, totals } = report;
  return (
    <div className="PROTEORISK-facts">
      <div className="PROTEORISK-fact-group">
        <span className="PROTEORISK-fact-group-title">Sample</span>
        <Fact label="Sample type" value="Blood plasma" />
        <Fact label="Age" value={facts.age === null ? 'Not recorded' : `${facts.age} years`} muted={facts.age === null} />
        <Fact label="Sex" value={facts.sex || 'Not recorded'} muted={!facts.sex} />
        <Fact label="BMI" value={facts.bmi === null ? 'Not given' : String(facts.bmi)} muted={facts.bmi === null} />
      </div>
      <div className="PROTEORISK-fact-group">
        <span className="PROTEORISK-fact-group-title">Measurement</span>
        <Fact label="Platform" value={coverage.platform} />
        <Fact
          label="Proteins measured"
          value={coverage.panelProteinCount === null ? 'Not recorded' : formatCount(coverage.panelProteinCount)}
          muted={coverage.panelProteinCount === null}
        />
        <Fact
          label="Used in the analysis"
          value={`${formatCount(coverage.observedProteinCount)} (${formatPercent(coverage.coverageFraction)})`}
        />
      </div>
      <div className="PROTEORISK-fact-group">
        <span className="PROTEORISK-fact-group-title">Results</span>
        <Fact label="Results" value={formatCount(totals.results)} />
        <Fact label="Body systems" value={formatCount(report.systems.length)} />
        <Fact label="Moderate or high" value={formatCount(totals.notable)} />
      </div>
    </div>
  );
}

function Legend() {
  return (
    <div className="PROTEORISK-legend">
      {['typical', 'moderate', 'high'].map((status) => (
        <span key={status} className="PROTEORISK-legend-entry">
          <StatusIcon status={status} size={13} />
          {STATUS_LABEL[status]}
        </span>
      ))}
    </div>
  );
}

function Highlights({ highlights }) {
  if (!highlights.length) {
    return (
      <div className="PROTEORISK-highlights-none">
        <StatusIcon status="typical" size={28} />
        <h3>No moderate or strong patterns</h3>
        <p>Your blood proteins look typical for every condition in this report.</p>
      </div>
    );
  }
  return (
    <ul className="PROTEORISK-highlights">
      {highlights.map((finding) => (
        <li key={finding.id} className="PROTEORISK-highlight">
          <ScoreGauge score100={finding.score100} tier={finding.tier} />
          <div className="PROTEORISK-highlight-text">
            <p>{highlightSentence(finding)}</p>
            <span className={`PROTEORISK-chip ${finding.tier}`}>
              <StatusIcon status={finding.tier} size={11} />
              {STATUS_LABEL[finding.tier]}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function SystemRows({ systems, selected, onSelect }) {
  return (
    <div className="PROTEORISK-system-rows">
      {systemsByAttention(systems).map((system) => (
        <button
          type="button"
          key={system.code}
          className={`PROTEORISK-system-row ${system.status} ${selected === system.code ? 'selected' : ''}`}
          onClick={() => onSelect(selected === system.code ? null : system.code)}
        >
          <span className={`PROTEORISK-system-badge ${system.status}`}>
            <SystemIcon code={system.code} size={20} />
          </span>
          <span className="PROTEORISK-system-text">
            <span className="PROTEORISK-system-head">
              <span className="PROTEORISK-system-name">{system.name}</span>
              <span className={`PROTEORISK-system-status ${system.status}`}>
                <StatusIcon status={system.status} size={11} />
                {STATUS_LABEL[system.status]}
              </span>
            </span>
            <span className="PROTEORISK-system-sentence">{systemSentence(system)}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

function ProteinPanel({ proteins }) {
  return (
    <div className="PROTEORISK-panel">
      <div className="PROTEORISK-panel-head">
        <span className="PROTEORISK-panel-title">Proteins behind this finding</span>
        <span className="PROTEORISK-panel-scale">
          <span className="PROTEORISK-panel-scale-label">Your level compared with the average</span>
          <ZAxis />
        </span>
        <span className="PROTEORISK-panel-z">z-score</span>
        <span />
      </div>
      {proteins.map((protein) => {
        const state = proteinState(protein);
        return (
          <div key={protein.proteinId} className={`PROTEORISK-panel-row ${protein.flagged ? 'flagged' : ''}`}>
            <span className="PROTEORISK-protein">
              <strong>{protein.geneName}</strong>
              {protein.proteinId !== protein.geneName && <span>{protein.proteinId}</span>}
            </span>
            <ZScoreBar z={protein.z} flagged={protein.flagged} />
            <span className="PROTEORISK-panel-zvalue">{formatSigned(protein.z)}</span>
            <span className={`PROTEORISK-panel-state ${state}`}>{PROTEIN_STATE_LABEL[state]}</span>
          </div>
        );
      })}
    </div>
  );
}

function FindingCard({ finding }) {
  return (
    <article className={`PROTEORISK-finding ${finding.tier}`}>
      <header className="PROTEORISK-finding-head">
        <h4>{finding.disease}</h4>
        <div className="PROTEORISK-finding-score">
          <ScoreBar score100={finding.score100} tier={finding.tier} />
          <span className={`PROTEORISK-finding-number ${finding.tier}`}>{finding.score100}</span>
        </div>
        <span className={`PROTEORISK-chip ${finding.tier}`}>{TIER_LABEL[finding.tier]}</span>
      </header>
      <p className="PROTEORISK-finding-text">{finding.interpretation}</p>
      {finding.proteins.length
        ? <ProteinPanel proteins={finding.proteins} />
        : <p className="PROTEORISK-finding-nopanel">{NO_PANEL}</p>}
    </article>
  );
}

function LowList({ findings }) {
  return (
    <div className="PROTEORISK-low">
      <span className="PROTEORISK-low-title">{lowTitle(findings.length)}</span>
      <ul>
        {findings.map((finding) => (
          <li key={finding.id}>
            <span>{finding.disease}</span>
            <span className="PROTEORISK-low-score">{finding.score100}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FindingsSection({ section, groups, counts, selected }) {
  const copy = SECTION_COPY[section];
  const shown = selected ? groups.filter((group) => group.system.code === selected) : groups;
  return (
    <section className="PROTEORISK-section PROTEORISK-findings">
      <header className="PROTEORISK-findings-head">
        <div>
          <h2>{copy.title}</h2>
          <p>{copy.intro}</p>
        </div>
        <div className="PROTEORISK-tier-counts">
          <span className="PROTEORISK-chip high">{`High ${counts.high}`}</span>
          <span className="PROTEORISK-chip moderate">{`Moderate ${counts.moderate}`}</span>
          <span className="PROTEORISK-chip low">{`Low ${counts.low}`}</span>
          <span className="PROTEORISK-tier-note">{resultsNote(counts)}</span>
        </div>
      </header>
      {shown.length === 0 && (
        <p className="PROTEORISK-findings-empty">No results for this body system in this section.</p>
      )}
      {shown.map((group) => (
        <div key={group.system.code} className="PROTEORISK-group">
          <div className="PROTEORISK-group-bar">
            <SystemIcon code={group.system.code} size={18} />
            <span className="PROTEORISK-group-name">{group.system.name}</span>
            <span className="PROTEORISK-group-note">{groupNote(group)}</span>
          </div>
          {group.notable.map((finding) => <FindingCard key={finding.id} finding={finding} />)}
          {group.low.length > 0 && <LowList findings={group.low} />}
        </div>
      ))}
    </section>
  );
}

function Technical({ coverage, technical }) {
  const spreadAvailable = technical && !technical.error;
  return (
    <details className="PROTEORISK-section PROTEORISK-technical">
      <summary>
        <span>Technical details</span>
        <i className="fas fa-chevron-down" aria-hidden="true" />
      </summary>
      <p>These numbers describe the measurement of your sample. They are included for completeness and for your doctor.</p>
      <div className="PROTEORISK-technical-grid">
        <Fact
          label="Proteins on the panel"
          value={coverage.panelProteinCount === null ? 'Not recorded' : formatCount(coverage.panelProteinCount)}
          muted={coverage.panelProteinCount === null}
        />
        <Fact
          label="Proteins matched to the analysis"
          value={coverage.matchedProteinCount === null
            ? 'Not recorded'
            : `${formatCount(coverage.matchedProteinCount)} of ${formatCount(coverage.modelProteinCount)}`}
          muted={coverage.matchedProteinCount === null}
        />
        <Fact
          label="Proteins used in the analysis"
          value={`${formatCount(coverage.observedProteinCount)} (${formatPercent(coverage.coverageFraction)})`}
        />
        <Fact
          label="Profile spread"
          value={spreadAvailable ? technical.residualSpread.toFixed(2) : 'Not available'}
          muted={!spreadAvailable}
        />
        <Fact
          label="Proteins more than 3 SD from the average"
          value={spreadAvailable ? formatPercent(technical.beyondThreeSdFraction) : 'Not available'}
          muted={!spreadAvailable}
        />
      </div>
      <p className="PROTEORISK-technical-note">
        After your protein levels are adjusted for age, sex and BMI, profile spread shows how widely they vary
        around the average of the general population. The share of proteins more than 3 standard deviations (SD)
        from that average shows how many of them are far from it.
      </p>
      {technical && technical.error && <p className="PROTEORISK-technical-error">{technical.error}</p>}
    </details>
  );
}

export default function ReportView({ report, facts, technical = null, actions = null, notice = null }) {
  const [selected, setSelected] = useState(null);
  const listsRef = useRef(null);
  const flagged = useMemo(
    () => report.systems.filter((system) => system.status !== 'typical').length,
    [report],
  );
  const selectedSystem = report.systems.find((system) => system.code === selected) || null;

  const select = (code) => {
    setSelected(code);
    if (code && listsRef.current) {
      window.requestAnimationFrame(() => listsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  };

  return (
    <div className="PROTEORISK-report">
      {notice}
      <section className="PROTEORISK-section PROTEORISK-glance">
        <div className="PROTEORISK-glance-head">
          <div>
            <h2>Your results at a glance</h2>
            <p>
              {headline(report.systems)}
              {' '}
              {`Based on ${formatCount(report.coverage.observedProteinCount)} proteins measured in your blood plasma and compared with data from the general population.`}
            </p>
          </div>
          {actions && <div className="PROTEORISK-report-actions">{actions}</div>}
        </div>
        <Facts report={report} facts={facts} />
      </section>

      <div className="PROTEORISK-glance-grid">
        <section className="PROTEORISK-section PROTEORISK-wheel-card">
          <h2>
            Body systems
            {flagged > 0 && <span className="PROTEORISK-count-badge">{flagged}</span>}
          </h2>
          <BodySystemWheel
            systems={report.systems}
            typical={report.typicalSystems}
            selected={selected}
            onSelect={select}
          />
          <Legend />
        </section>
        <section className="PROTEORISK-section PROTEORISK-highlights-card">
          <h2>Report highlights</h2>
          <Highlights highlights={report.highlights} />
        </section>
      </div>

      <section className="PROTEORISK-section">
        <h2>What each body system shows</h2>
        <SystemRows systems={report.systems} selected={selected} onSelect={select} />
      </section>

      <div ref={listsRef} className="PROTEORISK-lists">
        {selectedSystem && (
          <div className="PROTEORISK-filter">
            <SystemIcon code={selectedSystem.code} size={16} />
            <span>{`Showing ${selectedSystem.name}`}</span>
            <button type="button" onClick={() => setSelected(null)}>
              <i className="fas fa-times" aria-hidden="true" />
              Show all body systems
            </button>
          </div>
        )}
        {SECTIONS.map(({ section }) => (
          <FindingsSection
            key={section}
            section={section}
            groups={report.sections[section]}
            counts={report.counts[section]}
            selected={selected}
          />
        ))}
      </div>

      <Technical coverage={report.coverage} technical={technical} />

      <div className="PROTEORISK-closing">
        <div className="PROTEORISK-closing-card">
          <h3>
            <i className="fas fa-user-md" aria-hidden="true" />
            Discuss with your doctor
          </h3>
          <p>{DOCTOR_NOTE}</p>
        </div>
        <p className="PROTEORISK-footnote">{SCORE_FOOTNOTE}</p>
      </div>
    </div>
  );
}
